import { getConsentMode } from '../services/consentMode';
import { getSessionStorage } from '../utils/web/storageAvailability';

interface SDKPersistence {
    clear?: () => void;
    set_disabled?: (disabled: boolean) => void;
}

/** Narrow adapter for posthog-js's published unminified entrypoint. Public shutdown flushes queues;
 * opt_out writes a consent cookie, and before_send runs before batching, not sending.
 * Keep this contract covered against the installed real SDK when upgrading it.
 */
interface GuardedSDK {
    __loaded?: boolean;
    _onRemoteConfig?: (...args: unknown[]) => void;
    _remoteConfigLoader?: { stop?: () => void };
    sessionManager?: {
        _window_id_storage_key?: string;
        _primary_window_exists_storage_key?: string;
    };
    _send_request?: (...args: unknown[]) => unknown;
    __request_queue?: unknown[];
    _requestQueue?: { _queue: unknown[]; _isPaused?: boolean; _clearFlushTimeout?: () => void };
    _retryQueue?: {
        _queue: unknown[];
        _poller?: ReturnType<typeof setTimeout>;
        _isPolling?: boolean;
        _onlineListener?: EventListener;
        _offlineListener?: EventListener;
    };
    persistence?: SDKPersistence;
    sessionPersistence?: SDKPersistence;
}

export function guardPostHogTransport(instance: object): () => void {
    const sdk = instance as GuardedSDK;
    let stopped = false;
    const send = sdk._send_request;
    if ('config' in instance && typeof send !== 'function') {
        throw new Error('Unsupported PostHog transport: the unminified SDK entrypoint is required');
    }
    if (send) {
        sdk._send_request = (...args) => {
            if (!stopped && getConsentMode() === 'full') return send.apply(instance, args);
            return undefined;
        };
    }
    return () => {
        stopped = true;
        sdk.__loaded = false;
        sdk._remoteConfigLoader?.stop?.();
        sdk._onRemoteConfig = () => undefined;
        for (const key of [
            sdk.sessionManager?._window_id_storage_key,
            sdk.sessionManager?._primary_window_exists_storage_key,
        ]) {
            if (key) getSessionStorage().removeItem(key);
        }
        sdk.__request_queue = [];
        if (sdk._requestQueue) {
            sdk._requestQueue._queue = [];
            sdk._requestQueue._isPaused = true;
            sdk._requestQueue._clearFlushTimeout?.();
        }
        if (sdk._retryQueue) {
            sdk._retryQueue._queue = [];
            clearTimeout(sdk._retryQueue._poller);
            sdk._retryQueue._isPolling = false;
            if (sdk._retryQueue._onlineListener)
                window.removeEventListener('online', sdk._retryQueue._onlineListener);
            if (sdk._retryQueue._offlineListener)
                window.removeEventListener('offline', sdk._retryQueue._offlineListener);
        }
        for (const persistence of [sdk.persistence, sdk.sessionPersistence]) {
            persistence?.set_disabled?.(true);
            persistence?.clear?.();
        }
    };
}
